import React, { ReactNode, useEffect, useState } from 'react'
import { Button, InputGroup, Modal } from '@systemeio/components'
import { PlusCircleIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { COMMUNITY_TITLE_MAX_LENGTH } from 'shared/constants'
import { httpClient } from '../../../shared/api/http-client'
import { BadRequest } from '../../../shared/errors/bad-request'
import { InternalError } from '../../../shared/errors/internal-error'
import { generatePathFromName } from '../hooks/path-utils'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'
import { TopicInterface } from '../types/topic-interface'

type UpdateTopicProps = {
  topic: TopicInterface
  onCloseModal: () => void
  onUpdateSuccess: () => void
}

function UpdateTopic({ topic, onCloseModal, onUpdateSuccess }: UpdateTopicProps): ReactNode {
  const { t } = useTranslation()
  const router = useRouter()
  const { topicPath, communityPath } = router.query
  const [opened, setOpened] = useState(true)
  const [name, setName] = useState(topic.name)
  const [nameError, setNameError] = useState('')
  const [path, setPath] = useState(topic.path)
  const [pathError, setPathError] = useState('')
  const [error, setError] = useState('')
  const [isButtonActive, setIsButtonActive] = useState(false)

  const { data: community, mutate } = useCommunityWithTopics()

  function changeName(e: React.ChangeEvent<HTMLInputElement>): void {
    setName(e.target.value)
    setNameError('')
  }

  function changePath(e: React.ChangeEvent<HTMLInputElement>): void {
    setPath(e.target.value)
    setPathError('')
  }

  function convertNameToPath(e: React.FocusEvent<HTMLInputElement>): void {
    if (!e.target.value) {
      setPath(generatePathFromName(name))
      setPathError('')
    }
  }

  const clearForm = (): void => {
    setName('')
    setPath('')
  }

  const handleSendForm = async (): Promise<void> => {
    if (!community) {
      return
    }
    try {
      const { data } = await httpClient.patch<TopicInterface>(`/api/community/topic/${topic.id}`, {
        name,
        path,
      })
      await mutate(
        {
          ...community,
          topics: community.topics.map(t => (t.id === data.id ? data : t)),
        },
        true,
      )
      onUpdateSuccess()
      setOpened(false)
      clearForm()
      if (
        topic.path !== path && // we change current topicPath
        topicPath === topic.path
      ) {
        await router.replace(`/${communityPath}/${path}`)
      }
    } catch (error_) {
      if (error_ instanceof BadRequest) {
        if (error_.errors?.fields?.path) {
          setPathError(error_.errors?.fields?.path.join('\n'))
        }
        if (error_.errors?.fields?.name) {
          setNameError(error_.errors?.fields?.name.join('\n'))
        }
        if (error_.errors.common && error_.errors.common.length > 0) {
          setError(error_.errors.common.join('\n'))
        }
      } else if (error_ instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      }
    }
  }

  function closeModal(): void {
    setOpened(false)
    onCloseModal()
  }

  useEffect(() => {
    if (name !== topic.name || path !== topic.path) {
      setIsButtonActive(true)
    } else {
      setIsButtonActive(false)
    }
  }, [name, path])

  return (
    <Modal.Root
      open={opened}
      onOpenChange={value => {
        if (!value) closeModal()
      }}
    >
      <Modal.Popup>
        <Modal.Header>
          <Modal.Title>{t('settings.update_topic.modal.title')}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-primary">
              {t('settings.form.name.label')}
            </label>
            <InputGroup.Root>
              <InputGroup.Input
                value={name}
                onChange={changeName}
                maxLength={COMMUNITY_TITLE_MAX_LENGTH}
              />
            </InputGroup.Root>
            {nameError && <p className="text-sm text-text-error">{nameError}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-primary">
              {t('settings.form.path.label')}
            </label>
            <InputGroup.Root>
              <InputGroup.Input
                value={path}
                onChange={changePath}
                onFocus={convertNameToPath}
                maxLength={32}
              />
            </InputGroup.Root>
            {pathError && <p className="text-sm text-text-error">{pathError}</p>}
          </div>
          {error && <p className="text-sm text-text-error">{error}</p>}
        </div>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSendForm} disabled={!isButtonActive}>
            <PlusCircleIcon className="size-5" />
            {t('settings.update_topic.modal.title')}
          </Button>
        </Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}

export default UpdateTopic
